import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/site/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Servizi" },
  { to: "/chi-siamo", label: "Chi siamo" },
  { to: "/lavori", label: "Lavori" },
  { to: "/guide", label: "Guide" },
  { to: "/zone-servite", label: "Zone servite" },
  { to: "/contatti", label: "Contatti" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled || open
            ? "bg-av-black/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
          <Logo className="h-9 sm:h-10" />

          <nav className="hidden lg:flex items-center gap-9" aria-label="Menu principale">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide link-underline pb-1 ${
                    isActive ? "text-av-lime" : "text-av-white/85 hover:text-av-white"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contatti"
              data-testid="header-cta-sopralluogo"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-5 py-2.5 text-sm font-semibold hover:bg-av-lime transition-colors duration-300"
            >
              Richiedi un sopralluogo
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              aria-expanded={open}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/20 text-av-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-30 bg-av-black lg:hidden pt-24 px-6 grain"
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
          >
            <nav className="relative z-10 flex flex-col gap-1" aria-label="Menu mobile">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.05 }}
                >
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block font-display text-4xl py-3 border-b border-white/10 ${
                        isActive ? "text-av-lime" : "text-av-white"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contatti"
                data-testid="mobile-cta-sopralluogo"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-4 text-base font-semibold"
              >
                Richiedi un sopralluogo <ArrowUpRight size={18} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
